<template>
  <section id="blog" class="sofax surface-alt">
    <div class="section-space">
      <div class="shell">
        <!-- Heading left, one button right, as on the template's blog section -->
        <div class="mb-14 flex flex-wrap items-center justify-between gap-8 lg:mb-20">
          <div class="max-w-3xl">
            <h2 class="mb-4">{{ t('blog.homepageTitle') }}</h2>
            <p class="text-lg">{{ t('blog.homepageSubtitle') }}</p>
          </div>
          <SofaxBtn :to="blogHomePath(blog)" :label="t('blog.viewAllPosts')" variant="dark" />
        </div>

        <!-- Loading -->
        <div v-if="loading" class="flex flex-col items-center py-12 text-center" role="status">
          <span
            class="mb-4 inline-block size-8 animate-spin rounded-full border-2 border-line border-t-accent dark:border-gray-700 dark:border-t-accent"
            aria-hidden="true"
          />
          <span class="text-base text-muted dark:text-gray-400">{{ t('blog.loading') }}</span>
        </div>

        <!-- No posts -->
        <div
          v-else-if="posts.length === 0"
          class="mx-auto max-w-xl rounded-[1.25rem] border border-line bg-white px-6 py-16 text-center dark:border-gray-700 dark:bg-gray-900"
        >
          <i class="bi bi-journal-text mb-5 block text-5xl text-muted dark:text-gray-500" aria-hidden="true" />
          <p class="display-heading display-heading-4 mb-3">{{ t('blog.noPosts') }}</p>
          <p class="text-base">{{ t('blog.noPostsDescription') }}</p>
        </div>

        <!-- Three latest articles -->
        <ul v-else class="grid grid-cols-1 gap-[1.875rem] md:grid-cols-2 lg:grid-cols-3">
          <li v-for="post in posts.slice(0, 3)" :key="post.id">
            <BlogCard :post="post" :max-tags="2" layout="vertical" />
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { blogHomePath, blogLocaleFor } from '~/constants/blog'
import type { BlogPost } from '~/types';

// Composables
const { t, locale } = useI18n()

// The Czech and German homepages show their own blog, every other language the English one.
const blog = computed(() => blogLocaleFor(locale.value))

// Use lazy fetch for better performance - don't await to prevent blocking rendering
const { data: posts, pending: loading } = useLazyFetch<BlogPost[]>('/api/blog', {
  query: {
    is_published: true,
    locale: blog,
    limit: 10, // Get 4 latest posts for homepage
    offset: 0
  },
  default: () => [],
  server: true
})
</script>
